import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getGameStory,
  getUserProgress,
  checkUserActive,
  getActiveStory,
} from "../utils/api";
import { recordAction } from "../utils/userActionApi";
import LoadingState from "../components/LoadingState";
// images
import gameBackground from "../assets/images/game-background.png";
import animeCharacter from "../assets/images/anime-character.png";
// components
import MessageBubble from "../components/game/MessageBubble";
import AnswerModal from "../components/game/AnswerModal";
import AlertModal from "../components/game/AlertModal";
import ErrorHandler from "../components/game/ErrorHandler";
import SplashScreen from "../components/game/SplashScreen";
import TextInputModal from "../components/game/TextInputModal";
import InputAnswerAlert from "../components/game/InputAnswerAlert";
import HintPage from "../components/game/HintPage";
// hooks
import useGameState from "../hooks/useGameState";

// Function to get the character image - always returns the same image
const getCharacterImage = () => {
  return animeCharacter;
};

// Add keyframe animation style
const pulseAnimation = `
@keyframes pulse {
  0% {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 0 10px rgba(0, 255, 0, 0.2);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 255, 0, 0.5);
    transform: scale(1.05);
  }
  100% {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 0 10px rgba(0, 255, 0, 0.2);
    transform: scale(1);
  }
}
`;

const Game = () => {
  const navigate = useNavigate();
  const { storyId } = useParams();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [gameStory, setGameStory] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUserActive, setIsUserActive] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // Add state for showing debug info
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  // Add state for game completion and alert
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showCompletionAlert, setShowCompletionAlert] = useState(false);
  // Remove intro and conclusion message states
  const [showDescription, setShowDescription] = useState(true);
  const [showNextButton, setShowNextButton] = useState(false);
  // Add state for showing splash screen
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  const [showTextInputModal, setShowTextInputModal] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showAnswerAlert, setShowAnswerAlert] = useState(false);
  const [showHintPage, setShowHintPage] = useState(false);
  const [currentHintImage, setCurrentHintImage] = useState("");

  // Use custom hook for game state management
  const {
    showQuestions,
    selectedAnswer,
    showAnswerModal,
    answerInput,
    showSuccessAlert,
    showFailureAlert,
    handleNext,
    handleCloseSuccessAlert,
    handleCloseFailureAlert,
    setShowQuestions,
    setMessages: setHookMessages, // Renamed to avoid conflicts
    messages: hookMessages, // Access the hook's messages state
  } = useGameState();

  // Log state on render for debugging
  console.log("Game component state:", {
    hookShowQuestions: showQuestions,
    componentMessages: messages.length,
    hookMessages: hookMessages?.length || 0,
    currentQuestion: currentQuestion?.questionText,
  });

  // Synchronize messages from component to hook when they change
  useEffect(() => {
    if (
      messages.length > 0 &&
      setHookMessages &&
      (!hookMessages ||
        JSON.stringify(messages) !== JSON.stringify(hookMessages))
    ) {
      console.log("Syncing component messages to hook:", messages);
      setHookMessages(messages);
    }
  }, [messages, hookMessages, setHookMessages]);

  // Check if user is logged in and load game data
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const roomNumber = localStorage.getItem("roomNumber");
    const storedStoryId = localStorage.getItem("storyId");

    // If we're on the /game route and have a storedStoryId, redirect to the specific story URL
    if (!storyId && storedStoryId && window.location.pathname === "/game") {
      console.log(
        `On /game route with stored storyId ${storedStoryId}, redirecting to specific story URL`
      );
      navigate(`/story/${storedStoryId}`, { replace: true });
      return;
    }

    if (!userId || !roomNumber) {
      console.log("No active user session found");
      
      // If we have a storyId in URL, save it and redirect to /home for registration
      if (storyId) {
        console.log("Saving storyId and redirecting to home page for registration");
        localStorage.setItem("storyId", storyId);
        navigate("/home");
        return;
      }
      
      // Otherwise redirect to welcome page
      console.log("No storyId found, redirecting to welcome page");
      navigate("/");
      return;
    }

    // If the URL has a storyId parameter, use it and update localStorage
    if (storyId) {
      console.log(`Using storyId from URL: ${storyId}`);
      localStorage.setItem("storyId", storyId);
    }
    // If no storyId in URL but we have one in localStorage, update the URL for better navigation
    else if (storedStoryId) {
      console.log(
        `No storyId in URL, but found in localStorage: ${storedStoryId}`
      );
      // Update URL to match the stored storyId without a full page reload
      navigate(`/story/${storedStoryId}`, { replace: true });
    }

    // Load game data
    loadGameData();

    // Set up interval to check active status
    const activeCheckInterval = setInterval(checkActiveStatus, 30000); // Check every 30 seconds

    return () => clearInterval(activeCheckInterval);
  }, [navigate, storyId]);

  // Function to check if the user has been activated by admin
  const checkActiveStatus = async () => {
    try {
      const userId = localStorage.getItem("userId");

      if (!userId) return;

      // Check the user's active status
      const response = await checkUserActive(userId);
      console.log("Active status check response:", response);

      if (response.success) {
        // Allow the game to load even if isActive is false
        // This helps with testing and ensures users can always access the game
        // Remove any active status restrictions
        setIsUserActive(true);
      }
    } catch (err) {
      console.error("Failed to check active status:", err);
      // Default to active to prevent blocking gameplay
      setIsUserActive(true);
    }
  };

  // Function to load game data from the backend
  const loadGameData = async () => {
    try {
      setIsLoading(true);
      setError("");

      // First check if the user is active - but don't block on this
      await checkActiveStatus();

      // Get the story ID prioritizing the URL parameter
      let targetStoryId = storyId;

      // If no storyId in URL, try from localStorage next
      if (!targetStoryId) {
        targetStoryId = localStorage.getItem("storyId");
        console.log("Using storyId from localStorage:", targetStoryId);
      }

      // If still no storyId, get the active story from the server as last resort
      if (!targetStoryId) {
        try {
          console.log("No storyId found, requesting active story from server");
          const activeStoryResponse = await getActiveStory();
          console.log("Active story response:", activeStoryResponse);

          if (
            activeStoryResponse.success &&
            activeStoryResponse.data &&
            activeStoryResponse.data.length > 0
          ) {
            // Take the first active story
            targetStoryId = activeStoryResponse.data[0].storyId;
            console.log("Using first active story:", targetStoryId);
            // Save it in local storage for future reference
            localStorage.setItem("storyId", targetStoryId);
          }
        } catch (storyErr) {
          console.error("Error fetching active story:", storyErr);
        }
      }

      // If we still don't have a story ID, use a fallback ID
      if (!targetStoryId) {
        console.warn("No story ID found, using fallback story ID");
        targetStoryId = "default-story-id"; // You can replace this with an actual default story ID
        localStorage.setItem("storyId", targetStoryId);
      }

      console.log("Final target story ID:", targetStoryId);

      // Load the game story with the target ID
      const storyData = await getGameStory(targetStoryId);
      console.log("Full story data from backend:", storyData);

      if (!storyData.success || !storyData.data) {
        console.error("Failed to load story data:", storyData);
        throw new Error("宝探しデータの読み込みに失敗しました。");
      }

      // Update game story with our fixed character image
      const updatedStoryData = {
        ...storyData.data,
        defaultCharacterImageUrl: getCharacterImage(),
      };

      // Make sure we have questions data
      if (
        !updatedStoryData.questionsAnswers ||
        !Array.isArray(updatedStoryData.questionsAnswers) ||
        updatedStoryData.questionsAnswers.length === 0
      ) {
        console.error("No questions found in the story data or invalid format");

        // Create a basic demo question if none exists
        updatedStoryData.questionsAnswers = [
          {
            questionId: "demo-question-1",
            questionType: "multiple_choice",
            questionText: "恵みの泉へようこそ！始める準備はできましたか？",
            multipleChoiceOptions: [
              { value: "yes", text: "はい、始めましょう！", isCorrect: true },
              {
                value: "no",
                text: "いいえ、少し待ってください",
                isCorrect: false,
              },
            ],
            characterImageUrl: getCharacterImage(),
          },
        ];
      }

      // Set the game story data
      setGameStory(updatedStoryData);

      // Try to get user progress, but don't throw an error if it fails
      try {
        const progressData = await getUserProgress();
        console.log("User progress data:", progressData);

        if (progressData.success && progressData.data) {
          setProgress(progressData.data);
        } else {
          console.warn("Progress data not found or invalid");
        }
      } catch (progressErr) {
        console.error("Error loading user progress:", progressErr);
      }

      // Instead of intro messages, add a story description message
      if (updatedStoryData.description) {
        setMessages([
          {
            text: updatedStoryData.description,
            characterImage: getCharacterImage(),
          },
        ]);
        // Make sure we start in description mode with next button visible
        setShowDescription(true);
        setShowNextButton(true);
        // Ensure questions are not shown yet
        setShowQuestions(false);
      } else {
        // If no description, show a default welcome message
        setMessages([
          {
            text: `${
              updatedStoryData.title || "宝探しゲーム"
            }へようこそ！ゲームを始めましょう。`,
            characterImage: getCharacterImage(),
          },
        ]);
        // Make sure we start in description mode with next button visible
        setShowDescription(true);
        setShowNextButton(true);
        // Ensure questions are not shown yet
        setShowQuestions(false);
      }

      console.log("Setting initial game state:", {
        description:
          updatedStoryData.description ||
          `${updatedStoryData.title || "宝探しゲーム"}へようこそ！`,
        showDescription: true,
        showNextButton: true,
        showQuestions: false,
        questionsCount: updatedStoryData.questionsAnswers?.length,
      });

      // Set the first question (but don't show it yet - wait for user to click Next after description)
      if (
        updatedStoryData.questionsAnswers &&
        updatedStoryData.questionsAnswers.length > 0
      ) {
        setCurrentQuestion(updatedStoryData.questionsAnswers[0]);
        setCurrentQuestionIndex(0);
        console.log(
          "Initial question set:",
          updatedStoryData.questionsAnswers[0]
        );
      }
    } catch (error) {
      console.error("Error loading game data:", error);
      setError(
        error.message ||
          "ゲームデータの読み込みに失敗しました。もう一度お試しください。"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Effect for scrolling to bottom when messages change
  useEffect(() => {
    // Only scroll to bottom if we have multiple messages
    // If it's the first message (description), we want to stay at the top
    if (messages.length > 1) {
      scrollToBottom();
    } else {
      // Ensure we are at the top for the first message
      const messageContainer = document.querySelector('.overflow-y-auto');
      if (messageContainer) {
        messageContainer.scrollTop = 0;
      }
    }
  }, [messages]);

  // Function to scroll to the bottom of messages
  // Scrolls so the new message appears just above the "Next Clue" button area
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const messageContainer = document.querySelector('.overflow-y-auto');
      if (messageContainer) {
        // Calculate the scroll position to show the new message above the button
        // The button is at "bottom-16" (64px from bottom) + button height (~48px) + some padding
        // So we want to leave about 120-130px of space at the bottom
        const targetScrollTop = messagesEndRef.current.offsetTop - messageContainer.clientHeight + 130;
        messageContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      }
    }
  };

  // Handle file selection for image upload answers
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileToUpload(e.target.files[0]);
    }
  };

  // User inactive overlay - modified to never show
  const renderInactiveOverlay = () => {
    // Always return null to prevent the overlay from showing
    return null;

    // Original code commented out:
    /*
    if (!isUserActive && !isLoading) {
      return (
        <div className="absolute inset-0 bg-black/80 z-50 flex flex-col items-center justify-center p-6 text-white">
          <div className="bg-gray-800 p-6 rounded-xl max-w-xs mx-auto text-center">
            <h2 className="text-xl font-bold mb-4">アカウント承認待ち</h2>
            <p className="mb-4">管理者があなたの情報を確認中です。しばらくお待ちください。</p>
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mx-auto"></div>
          </div>
        </div>
      );
    }
    return null;
    */
  };

  // Sponsor banner component
  const SponsorBanner = () => (
    <div className="bg-red-500 text-white py-3 px-4 text-center relative z-50 shadow-md flex-shrink-0">
      宝探しゲーム - 隠された宝物を見つけよう！
    </div>
  );

  // Update moveToNextQuestion to handle multiple choice questions differently
  const moveToNextQuestion = () => {
    if (!gameStory || !gameStory.questionsAnswers) {
      console.warn("No game story data or clues available");
      setError("手がかりが見つかりません。宝探しをやり直してください。");
      return;
    }

    console.log(
      "Moving to next clue. Current index:",
      currentQuestionIndex,
      "Total clues:",
      gameStory.questionsAnswers.length
    );

    // Check if there are more questions
    if (currentQuestionIndex < gameStory.questionsAnswers.length - 1) {
      // Increment to the next question index
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = gameStory.questionsAnswers[nextIndex];

      // Validate the next question
      if (!nextQuestion ) {
        console.error("Invalid next question:", nextQuestion);
        setError(
          "次の手がかりが無効です。ゲーム管理者にお問い合わせください。"
        );
        return;
      }

      console.log(
        `Moving to clue ${nextIndex + 1} of ${
          gameStory.questionsAnswers.length
        }:`,
        nextQuestion
      );

      // Set the new question as current
      setCurrentQuestion(nextQuestion);
      setCurrentQuestionIndex(nextIndex);

      // Handle question based on its type
      const questionType = (nextQuestion.questionType || "").toUpperCase();

      // For multiple choice questions, only show the options without the question text
      if (questionType === "MULTIPLE_CHOICE") {
        try {
          // Determine where options are stored
          let options =
            nextQuestion.multipleChoiceOptions || nextQuestion.options || [];

          // Make sure options is an array
          if (!Array.isArray(options) || options.length === 0) {
            console.error("Options is not an array or is empty:", options);
            options = [
              { text: "選択肢 1", isCorrect: false },
              { text: "選択肢 2", isCorrect: true },
              { text: "選択肢 3", isCorrect: false },
            ];
          }

          // Add options as a single message with all choices
          // Formatted as a user message on the right side
          setMessages((prev) => [
            ...prev,
            {
              options: options.map((option, index) => {
                try {
                  const optionLabel = String.fromCharCode(65 + index); // A=65, B=66, etc.
                  const optionText =
                    option.text ||
                    option.content ||
                    option.label ||
                    `選択肢 ${index + 1}`;
                  return {
                    label: optionLabel,
                    text: optionText,
                    ...option,
                  };
                } catch (optionErr) {
                  console.error("Error processing option:", optionErr);
                  return {
                    label: String.fromCharCode(65 + index),
                    text: `選択肢 ${index + 1}`,
                    isCorrect: false,
                  };
                }
              }),
              isUser: true, // Position on right side
              isMultipleChoice: true, // Special flag for multiple choice styling
              timestamp: new Date(),
            },
          ]);
        } catch (err) {
          console.error("Error setting up multiple choice question:", err);
          setError("次の手がかりが無効です。ゲーム管理者にお問い合わせください。");
        }
      }
      // For file upload questions, show a file upload UI
      else if (questionType === "FILE_UPLOAD") {
        // Display question first
        setMessages((prev) => [
          ...prev,
          {
            text: nextQuestion.questionText,
            isUser: false,
            characterImage: getCharacterImage(),
            timestamp: new Date(),
            isQuestion: true,
          },
        ]);

        // Then add file upload UI on right side
        setMessages((prev) => [
          ...prev,
          {
            isUser: true,
            isFileUpload: true, // Special flag for file upload UI
            timestamp: new Date(),
          },
        ]);
      }
      // For other question types, display the question as a chat message
      else {
        setMessages((prev) => [
          ...prev,
          {
            text: nextQuestion.questionText,
            isUser: false,
            characterImage: getCharacterImage(),
            mediaUrl:
              nextQuestion.questionType === "NO_ANSWER" ||
              nextQuestion.questionType === "no_answer"
                ? nextQuestion.mediaUrl
                : null,
            timestamp: new Date(),
            isQuestion: true,
          },
        ]);

        // For no_answer questions, don't show the next button
        if (
          nextQuestion.questionType === "NO_ANSWER" ||
          nextQuestion.questionType === "no_answer"
        ) {
          setShowNextButton(false); // Hide the next button
        } else {
          setShowNextButton(true); // Show the next button for other question types
        }
      }

      // Don't show the questions panel at the bottom anymore
      setShowQuestions(false);
    } else {
      // This is the last question - show game completion
      console.log("All clues have been solved! Game completed");

      // End the game
      setGameCompleted(true);
      setShowNextButton(false);

      // Add a completion message
      setMessages((prev) => [
        ...prev,
        {
          text: "おめでとうございます！すべての手がかりを見つけて、宝物を発見しました！",
          characterImage: getCharacterImage(),
          isFeedback: true,
          timestamp: new Date(),
        },
      ]);

      // After 2 seconds, show the completion alert
      setTimeout(() => {
        setShowCompletionAlert(true);
      }, 2000);
    }
  };

  // Update handleNextButtonClick for the first question
  const handleNextButtonClick = () => {
    // Add debug info
    console.log("handleNextButtonClick called with states:", {
      showDescription,
      showQuestions,
      currentQuestionIndex,
      totalQuestions: gameStory?.questionsAnswers?.length || 0,
    });

    // Transition from description to questions
    if (showDescription) {
      setShowDescription(false);
      if (gameStory?.questionsAnswers?.length > 0) {
        // Get the first question
        const firstQuestion = gameStory.questionsAnswers[0];
        setCurrentQuestionIndex(0);
        setCurrentQuestion(firstQuestion);

        // Handle question based on its type
        const questionType = (firstQuestion.questionType || "").toUpperCase();

        // For multiple choice questions, only show the options without the question text
        if (questionType === "MULTIPLE_CHOICE") {
          try {
            // Determine where options are stored
            let options =
              firstQuestion.multipleChoiceOptions || firstQuestion.options || [];

            // Make sure options is an array
            if (!Array.isArray(options) || options.length === 0) {
              console.error("Options is not an array or is empty:", options);
              options = [
                { text: "選択肢 1", isCorrect: false },
                { text: "選択肢 2", isCorrect: true },
                { text: "選択肢 3", isCorrect: false },
              ];
            }

            // Add options as a single message with all choices
            // Formatted as a user message on the right side
            setMessages((prev) => [
              ...prev,
              {
                options: options.map((option, index) => {
                  try {
                    const optionLabel = String.fromCharCode(65 + index); // A=65, B=66, etc.
                    const optionText =
                      option.text ||
                      option.content ||
                      option.label ||
                      `選択肢 ${index + 1}`;
                    return {
                      label: optionLabel,
                      text: optionText,
                      ...option,
                    };
                  } catch (optionErr) {
                    console.error("Error processing option:", optionErr);
                    return {
                      label: String.fromCharCode(65 + index),
                      text: `選択肢 ${index + 1}`,
                      isCorrect: false,
                    };
                  }
                }),
                isUser: true, // Position on right side
                isMultipleChoice: true, // Special flag for multiple choice styling
                timestamp: new Date(),
              },
            ]);
          } catch (err) {
            console.error("Error setting up multiple choice question:", err);
            setError("次の手がかりが無効です。ゲーム管理者にお問い合わせください。");
          }
        }
        // For file upload questions, show a file upload UI
        else if (questionType === "FILE_UPLOAD") {
          // Display question first
          setMessages((prev) => [
            ...prev,
            {
              text: firstQuestion.questionText,
              isUser: false,
              characterImage: getCharacterImage(),
              timestamp: new Date(),
              isQuestion: true,
            },
          ]);

          // Then add file upload UI on right side
          setMessages((prev) => [
            ...prev,
            {
              isUser: true,
              isFileUpload: true, // Special flag for file upload UI
              timestamp: new Date(),
            },
          ]);
        }
        // For other question types, display the question as a chat message
        else {
          setMessages((prev) => [
            ...prev,
            {
              text: firstQuestion.questionText,
              isUser: false,
              characterImage: getCharacterImage(),
              mediaUrl:
                firstQuestion.questionType === "NO_ANSWER" ||
                firstQuestion.questionType === "no_answer"
                  ? firstQuestion.mediaUrl
                  : null,
              timestamp: new Date(),
              isQuestion: true,
            },
          ]);

          // Don't show the questions panel at the bottom
          setShowQuestions(false);

          // For no_answer questions, don't show the next button but show hint buttons instead
          if (
            firstQuestion.questionType === "NO_ANSWER" ||
            firstQuestion.questionType === "no_answer"
          ) {
            setShowNextButton(false); // Hide the next button
          }
        }
      } else {
        // No questions in the story - show empty state or completion
        console.warn("No questions found in story data");
        setShowCompletionAlert(true);
      }
    }
    // Move to next question after completing current question
    else {
      moveToNextQuestion();
    }
  };

  // Function to skip the current question without answering
  const handleSkipQuestion = () => {
    // Add a message indicating the question was skipped
    setMessages((prev) => [
      ...prev,
      {
        text: "この手がかりはあとで考えます...",
        isUser: true,
        timestamp: new Date(),
      },
    ]);

    // Briefly hide questions
    setShowQuestions(false);

    // After a short delay, move to the next question
    setTimeout(() => {
      moveToNextQuestion();
    }, 500);
  };

  // Toggle debug info display
  const toggleDebugInfo = () => {
    console.log("Toggling debug info, current state:", showDebugInfo);
    setShowDebugInfo((prev) => !prev);
  };

  // Function to reset error and reload game data
  const resetError = () => {
    setError("");
    loadGameData();
  };

  // Function to return to first page
  const handleReturnToHome = () => {
    // Clear the completion alert
    setShowCompletionAlert(false);

    // Navigate to the welcome page
    navigate("/");
  };

  // Add this function to handle file upload submissions
  const handleFileSubmit = () => {
    if (!fileToUpload) {
      setMessages((prev) => [
        ...prev,
        {
          text: "ファイルを選択してください。",
          isError: true,
        },
      ]);
      return;
    }

    setIsSubmitting(true);

    // Record the file upload action
    recordAction('file_upload', { 
      questionId: currentQuestion?.questionId,
      questionText: currentQuestion?.questionText,
      fileName: fileToUpload.name,
      fileSize: fileToUpload.size,
      fileType: fileToUpload.type
    });

    // Message to notify user that file is being uploaded
    setMessages((prev) => [
      ...prev,
      {
        text: `ファイル "${fileToUpload.name}" をアップロードしています...`,
        isUser: true,
      },
    ]);

    // Simulate file upload with timeout
    setTimeout(() => {
      // Success message after upload
      setMessages((prev) => [
        ...prev,
        {
          text: "ファイルが正常にアップロードされました！",
          characterImage: getCharacterImage(),
          isFeedback: true,
        },
      ]);

      // Hide questions and show next button
      setShowQuestions(false);
      setShowNextButton(true);

      // Reset file upload state
      setFileToUpload(null);
      setIsSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }, 1500);
  };

  // Add useEffect for animation style
  useEffect(() => {
    // Add the pulse animation style to the head
    const styleElement = document.createElement("style");
    styleElement.innerHTML = pulseAnimation;
    document.head.appendChild(styleElement);

    return () => {
      // Clean up the style element when component unmounts
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // Add a helper function to enable debug mode with a keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Press Ctrl+D to toggle debug mode
      if (e.ctrlKey && e.key === "d") {
        e.preventDefault();
        setShowDebugInfo((prev) => !prev);
        console.log("Debug mode toggled");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle start game from splash screen
  const handleStartGame = () => {
    setShowSplashScreen(false);
  };

  // Function to handle showing the hint page
  const handleShowHintPage = () => {
    // Get the hint image for the current question if available
    const hintImage = currentQuestion?.hintImage || "";
    setCurrentHintImage(hintImage);
    setShowHintPage(true);
  };

  // Function to handle going back from the hint page
  const handleBackFromHint = () => {
    setShowHintPage(false);
  };

  // Replace or modify existing handleGiveHint function
  const handleGiveHint = () => {
    // If the hint has an image, show the hint page
    if (currentQuestion?.hints) {
      handleShowHintPage();
    } else {
      // Otherwise just show hint text as before
      setMessages((prev) => [
        ...prev,
        {
          text:
            currentQuestion?.hint ||
            "この手がかりについてのヒントです。よく考えてみましょう。",
          isUser: false,
          characterImage: getCharacterImage(),
          isFeedback: true,
          timestamp: new Date(),
        },
      ]);
    }
  };

  // Add back the handleShowAnswer function
  const handleShowAnswer = () => {
    // Record the show answer action
    recordAction('show_answer', { 
      questionId: currentQuestion?.questionId,
      questionText: currentQuestion?.questionText
    });

    setMessages((prev) => [
      ...prev,
      {
        text: "正解を入力してください。",
        isUser: false,
        characterImage: getCharacterImage(),
        timestamp: new Date(),
      },
    ]);

    // Show next button after showing the answer prompt
    setShowNextButton(true);
  };

  const handleTextInputSubmit = (input) => {
    console.log("TextInput submitted:", input);
    setShowTextInputModal(false);
    
    // Check if answer is correct
    const isCorrect = input === currentQuestion.correctAnswer;
    
    // Record the text answer action
    recordAction('text_input', { 
      questionId: currentQuestion?.questionId,
      questionText: currentQuestion?.questionText,
      userAnswer: input,
      isCorrect: isCorrect
    });
    
    // Update state based on answer correctness
    if (isCorrect) {
      setIsCorrect(true);
    } else {
      setIsCorrect(false);
    }
    
    setShowAnswerAlert(true);
  };

  // Modify the MessageBubble component to render the special multiple choice format
  const MessageBubble = ({ message, index }) => {
    // Basic styling for message bubbles
    const userStyle =
      "bg-teal-500 text-white self-end rounded-tl-xl rounded-tr-xl rounded-bl-xl ml-12";
    const botStyle =
      "bg-purple-500 text-white self-start rounded-tr-xl rounded-tl-xl rounded-br-xl mr-12";
    const feedbackStyle = message.isFeedback
      ? "bg-amber-500 text-white self-start rounded-xl mr-12"
      : botStyle;

    // Special handling for multiple choice messages that show all options
    if (message.isMultipleChoice && message.options) {
      return (
        <div className="mb-4">
          <div className="flex flex-col items-end justify-end">
            <div className="bg-[#56c7d4] text-white self-end rounded-xl p-0 w-full max-w-[60%] overflow-hidden">
              {Array.isArray(message.options) ? (
                message.options.map((option, idx) => (
                  <div
                    key={idx}
                    className="p-3 relative cursor-pointer"
                    onClick={() => {
                      try {
                        // Handle option selection
                        const userMessage = {
                          text: `${option.label}. ${option.text}`,
                          isUser: true,
                          timestamp: new Date(),
                        };
                        setMessages((prev) => [...prev, userMessage]);

                        // Record option selection action
                        recordAction('option_select', {
                          questionId: currentQuestion?.questionId,
                          questionText: currentQuestion?.questionText,
                          selectedOption: `${option.label}. ${option.text}`,
                          isCorrect: option.isCorrect === true || option.correct === true
                        });

                        // After short delay, show response
                        setTimeout(() => {
                          try {
                            // Determine if answer is correct
                            const isCorrect =
                              option.isCorrect === true || option.correct === true;

                            // Create response message with styled feedback
                            const feedbackIcon = isCorrect ? "✓" : "✗";
                            const feedbackIntro = isCorrect ? "成功！" : "残念...";

                            // Get feedback text
                            const questionData = currentQuestion;
                            const feedbackText = isCorrect
                              ? questionData?.correctFeedback ||
                                option.feedback ||
                                "パズルを解きました！"
                              : option.feedback ||
                                questionData?.incorrectFeedback ||
                                "それは正しい解答ではありません。もう一度試してみてください！";

                            // Create response message
                            const responseMessage = {
                              text: `${feedbackIntro} ${feedbackIcon}\n${feedbackText}`,
                              isUser: false,
                              isFeedback: true,
                              characterImage: getCharacterImage(),
                              timestamp: new Date(),
                            };

                            setMessages((prev) => [...prev, responseMessage]);

                            // Show next button if answer is correct
                            if (isCorrect) {
                              setShowNextButton(true);
                            }
                          } catch (err) {
                            console.error("Error processing option selection:", err);
                            setError("選択処理中にエラーが発生しました。もう一度お試しください。");
                          }
                        }, 500);
                      } catch (err) {
                        console.error("Error handling option click:", err);
                        setError("選択肢の処理中にエラーが発生しました。もう一度お試しください。");
                      }
                    }}
                  >
                    <div className="font-bold">
                      {option.label}. {option.text}
                    </div>
                    {/* Add separator line except for the last item */}
                    {idx < message.options.length - 1 && (
                      <div className="absolute bottom-0 left-3 right-3 h-px bg-white/30"></div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-3 text-center">
                  選択肢の読み込みに問題がありました。もう一度試してください。
                </div>
              )}
              <div className="absolute bottom-1 right-1 text-xs text-white/70">
                あなた
              </div>
            </div>
            <span className="text-[15px] text-white font-bold">あなた</span>
          </div>
        </div>
      );
    }

    // Special handling for file upload messages
    if (message.isFileUpload) {
      return (
        <div className="mb-4">
          <div className="flex flex-col items-end justify-end">
            <div className="bg-[#56c7d4] text-white self-end rounded-xl p-4 w-full max-w-[60%] overflow-hidden">
              {fileToUpload ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium">
                    選択されたファイル: {fileToUpload.name}
                  </div>
                  <div className="flex justify-between">
                    <button
                      className="bg-white text-[#56c7d4] hover:bg-gray-100 text-sm font-medium py-1 px-3 rounded-full"
                      onClick={() => {
                        setFileToUpload(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                    >
                      キャンセル
                    </button>
                    <button
                      className="bg-white text-[#56c7d4] hover:bg-gray-100 text-sm font-medium py-1 px-3 rounded-full"
                      onClick={() => handleFileSubmit()}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "アップロード中..." : "アップロード"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-center font-medium">
                    写真をアップロード
                  </div>
                  <button
                    className="w-full bg-white text-[#56c7d4] hover:bg-gray-100 font-medium py-2 px-4 rounded-full"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    ファイルを選択
                  </button>
                </div>
              )}
              <div className="absolute bottom-1 right-1 text-xs text-white/70">
                あなた
              </div>
            </div>
            <span className="text-[15px] text-white font-bold">あなた</span>
          </div>
        </div>
      );
    }

    // Regular message bubbles
    return (
      <div className="mb-4">
        <div
          className={`flex ${message.isUser ? "justify-end" : "justify-start"}`}
        >
          {!message.isUser && message.characterImage && (
            <div className="w-10 h-10 rounded-full overflow-hidden mr-2 flex-shrink-0">
              <img
                src={message.characterImage}
                alt="キャラクター"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {message.isUser ? (
            <div
              className={`p-3 rounded-lg max-w-[75%] break-words ${userStyle}`}
            >
              {message.text}
            </div>
          ) : (
            <div className="flex flex-col">
              <div
                className={`p-3 rounded-lg max-w-[75%] break-words ${
                  message.isQuestion
                    ? "bg-purple-500 text-white self-start rounded-tr-xl rounded-tl-xl rounded-br-xl mr-12"
                    : feedbackStyle
                }`}
              >
                {message.text}
              </div>

              {/* Show mediaUrl image if present */}
              {message.mediaUrl && (
                <div className="rounded-xl overflow-hidden mt-1 max-w-[300px] h-[240px] ">
                  <img
                    src={message.mediaUrl}
                    alt="手がかりの画像"
                    className="w-full h-auto object-cover rounded-xl shadow-md"
                    onError={(e) => {
                      console.error(
                        "Failed to load media image:",
                        message.mediaUrl
                      );
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Function to render the hint page
  const renderHintPage = () => {
    return (
      <HintPage
        hints={currentQuestion?.hints}
        onAnswerInput={() => {
          setShowTextInputModal(true);
          setShowHintPage(false);
        }}
        onBack={handleBackFromHint}
      />
    );
  };

  if (isLoading) {
    return <LoadingState message="ゲームデータを読み込み中..." />;
  }

  if (error) {
    return <ErrorHandler error={error} resetError={resetError} />;
  }

  return (
    <>
      {/* Show splash screen if enabled */}
      {showSplashScreen && <SplashScreen onStartGame={handleStartGame} />}

      <div
        className="relative bg-cover bg-center bg-no-repeat max-w-[480px] mx-auto h-svh overflow-hidden flex flex-col"
        style={{
          backgroundImage: `url(${gameBackground})`,
          fontFamily: "Hiragino",
          opacity: showSplashScreen ? 0 : 1,
          transition: "opacity 0.5s ease-in-out",
        }}
      >
        {/* Inject animation keyframes */}
        <style dangerouslySetInnerHTML={{ __html: pulseAnimation }} />

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>

        {/* Sponsor Banner */}
        <SponsorBanner />

        {/* Inactive user overlay */}
        {renderInactiveOverlay()}

        {/* Debug toggle button (hidden in plain sight) */}
        <div
          className="absolute top-0 right-0 w-12 h-12 z-50 opacity-0"
          onClick={toggleDebugInfo}
        />

        {/* Game content */}
        <div className="flex-1 flex flex-col overflow-hidden relative z-10">
          {showHintPage ? (
            // Show hint page when active
            renderHintPage()
          ) : (
            // Regular game content
            <>
              {/* Messages container */}
              <div className="flex-1 overflow-y-auto px-4 py-5 pb-32">
                {messages.map((message, index) => (
                  <MessageBubble key={index} message={message} index={index} />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Debug info panel for development */}
              {showDebugInfo && (
                <div className="fixed top-14 right-0 left-0 mx-auto max-w-[300px] bg-black/90 text-green-400 p-2 rounded z-20 text-xs font-mono overflow-auto max-h-[50vh]">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold">デバッグ情報</span>
                    <button
                      onClick={toggleDebugInfo}
                      className="text-red-400 hover:text-red-300"
                    >
                      ✕
                    </button>
                  </div>
                  <div>
                    <p>Game state:</p>
                    <p>showDescription: {showDescription ? "true" : "false"}</p>
                    <p>showQuestions: {showQuestions ? "true" : "false"}</p>
                    <p>showNextButton: {showNextButton ? "true" : "false"}</p>
                    <p>currentQuestionIndex: {currentQuestionIndex}</p>
                    <p>
                      totalQuestions: {gameStory?.questionsAnswers?.length || 0}
                    </p>
                    {currentQuestion && (
                      <>
                        <p>タイプ: {currentQuestion.questionType}</p>
                        <p className="mt-1 border-t border-green-800 pt-1">
                          生データ:
                        </p>
                        <pre className="whitespace-pre-wrap break-all overflow-x-auto text-[8px]">
                          {JSON.stringify(currentQuestion, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Next button */}
              {showNextButton && (
                <div className="fixed bottom-16 left-0 right-0 px-4 flex justify-center space-x-4 z-10">
                  <button
                    onClick={handleNextButtonClick}
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-10 rounded-full shadow-md w-[200px] text-lg"
                    style={{
                      boxShadow:
                        "0 4px 6px rgba(0, 0, 0, 0.1), 0 0 15px rgba(0, 255, 0, 0.3)",
                      fontFamily: "Hiragino Kaku Gothic Pro, sans-serif",
                      animation: "pulse 2s infinite",
                    }}
                  >
                    {
                      showDescription
                        ? "始める" // "Start" when showing description
                        : "次の手がかり" // "Next Clue" for questions
                    }
                  </button>
                </div>
              )}

              {/* Two buttons for no_answer questions */}
              {!showNextButton &&
                currentQuestion &&
                (currentQuestion.questionType === "NO_ANSWER" ||
                  currentQuestion.questionType === "no_answer") && (
                  <div className="fixed bottom-16 left-0 right-0 px-4 flex justify-center space-x-4 z-10">
                    <button
                      onClick={() => setShowTextInputModal(true)}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-8 rounded-full shadow-md text-lg"
                      style={{
                        boxShadow:
                          "0 4px 6px rgba(0, 0, 0, 0.1), 0 0 15px rgba(63, 81, 181, 0.3)",
                        fontFamily: "Hiragino Kaku Gothic Pro, sans-serif",
                      }}
                    >
                      答えを入力
                    </button>
                    <button
                      onClick={handleGiveHint}
                      className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-8 rounded-full shadow-md text-lg"
                      style={{
                        boxShadow:
                          "0 4px 6px rgba(0, 0, 0, 0.1), 0 0 15px rgba(255, 193, 7, 0.3)",
                        fontFamily: "Hiragino Kaku Gothic Pro, sans-serif",
                      }}
                    >
                      ヒント1
                    </button>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Game completion alert */}
        {showCompletionAlert && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-xs mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">ゲーム完了！</h2>
              <p className="mb-6">
                おめでとうございます！すべての手がかりを見つけました。
              </p>
              <button
                onClick={handleReturnToHome}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg shadow-md transition"
              >
                ホームに戻る
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Input for text answers */}
      {showTextInputModal && (
        <TextInputModal
          onCancel={() => setShowTextInputModal(false)}
          onSubmit={handleTextInputSubmit}
          question={currentQuestion?.question}
        />
      )}

      {/* Answer Alert */}
      {showAnswerAlert && (
        <InputAnswerAlert
          isCorrect={isCorrect}
          onClose={() => {
            setShowAnswerAlert(false);
            if (isCorrect) {
              moveToNextQuestion();
            }
          }}
        />
      )}

      {/* Hidden file input for photo uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        style={{ display: "none" }}
      />
    </>
  );
};

export default Game;
